import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { Dashboard } from '../models/dashboard';
import { DashboardDataService } from '../services/dashboard-data.service';
import { DashboardFactory } from '../factory/dashboard.factory';
import { LoadingService } from '../../shared/services/loading.service';

@Injectable({ providedIn: 'root' })
export class DashboardStore {
  // DashboardDataService über Angulars inject-Funktion holen
  dashboardDataService = inject(DashboardDataService);
  // LoadingService ebenso injizieren, um Lade-Indikatoren zu steuern
  loadingService = inject(LoadingService);

  // Signal, das die aktuell geladenen Dashboards hält; initial mit einem "Welcome"-Dashboard
  dashboards = signal<Dashboard[]>([DashboardFactory.createWelcomeDashboard()]);

  /**
   * Lädt die Dashboards des Tenants und gibt ein Observable zurück.
   * Während des Ladevorgangs wird der Lade-Indikator über LoadingService angezeigt.
   * @returns Observable, das bei Erfolg eine Liste von Dashboard-Objekten liefert
   */
  loadDashboardsForTenant(): Observable<Dashboard[]> {
    // Lade-Indikator einschalten
    this.loadingService.show();

    return this.dashboardDataService
      .fetchDashboardsForTenant() // HTTP-Request an DashboardDataService
      .pipe(
        // Sobald der Stream abgeschlossen oder ein Fehler auftritt, Lade-Indikator ausschalten
        finalize(() => this.loadingService.hide()),
      );
  }

  /**
   * Lädt die Dashboards des aktuell angemeldeten Benutzers.
   * Hier wird direkt in das dashboards-Signal geschrieben, daher kein Rückgabe-Observable.
   */
  loadUserDashboards(): void {
    // Lade-Indikator einschalten
    this.loadingService.show();

    this.dashboardDataService
      .fetchUserDashboards() // HTTP-Request an DashboardDataService
      .pipe(
        // Lade-Indikator ausschalten, sobald Stream endet oder Fehler kommt
        finalize(() => this.loadingService.hide())
      )
      .subscribe((dashboards) => {
        // Wenn keine Dashboards zurückkommen, füge ein "Welcome"-Dashboard ein
        if (!dashboards || dashboards.length === 0) {
          dashboards = [DashboardFactory.createWelcomeDashboard()];
        }
        // Setze das Signal auf die geladene Dashboard-Liste
        this.dashboards.set(dashboards);
      });
  }

  /**
   * Lädt ein einzelnes Dashboard anhand der ID.
   * Gibt ein Observable<Dashboard> zurück, schaltet den Lade-Indikator sichtbar.
   * @param id - Die Dashboard-ID, die abgefragt werden soll
   * @returns Observable, das das gefundene Dashboard liefert
   */
  loadDashboardById(id: number): Observable<Dashboard> {
    // Lade-Indikator einschalten
    this.loadingService.show();

    return this.dashboardDataService.fetchDashboardById(id).pipe(
      // Lade-Indikator ausschalten, sobald Stream endet
      finalize(() => this.loadingService.hide())
    );
  }

  /**
   * Erzeugt ein neues Dashboard im Backend.
   * Gibt bei Erfolg true zurück, ansonsten Fehler.
   * @param dashboard - Das Dashboard-Objekt, das angelegt werden soll
   * @returns Observable<boolean>
   */
  createDashboard(dashboard: Dashboard): Observable<boolean> {
    // Lade-Indikator einschalten
    this.loadingService.show();

    return this.dashboardDataService.createDashboard(dashboard).pipe(
      // Auf 'true' mappen, wenn der POST erfolgreich war
      map(() => true),
      // Lade-Indikator ausschalten, sobald Stream endet
      finalize(() => this.loadingService.hide())
    );
  }

  /**
   * Aktualisiert ein bestehendes Dashboard im Backend.
   * Bei Erfolg wird das lokale Signal aktualisiert, damit die UI den neuen Stand zeigt.
   * @param id - ID des Dashboards, das aktualisiert werden soll
   * @param dashboard - Das geänderte Dashboard-Objekt
   * @returns Observable<boolean>
   */
  updateDashboard(id: number, dashboard: Dashboard): Observable<boolean> {
    // Lade-Indikator einschalten
    this.loadingService.show();

    return this.dashboardDataService.updateDashboard(dashboard, id).pipe(
      // tap erlaubt uns, seiteneffektfrei den Store zu aktualisieren
      tap(updatedDashboard => {
        // Ersetze in unserer Signal-Liste das Dashboard mit der gegebenen ID
        const updatedDashboards = this.dashboards().map(d =>
          d.id === id ? dashboard : d
        );
        this.dashboards.set(updatedDashboards);
      }),
      // Erfolgreiche Antwort in 'true' umwandeln
      map(() => true),
      // Lade-Indikator ausschalten, sobald Stream endet
      finalize(() => this.loadingService.hide())
    );
  }

  /**
   * Löscht ein Dashboard im Backend anhand seiner ID.
   * @param id - ID des Dashboards, das gelöscht werden soll
   * @returns Observable<boolean>
   */
  deleteDashboard(id: number): Observable<boolean> {
    // Lade-Indikator einschalten
    this.loadingService.show();

    return this.dashboardDataService.deleteDashboard(id).pipe(
      // Lade-Indikator ausschalten, sobald Stream endet
      finalize(() => this.loadingService.hide()),
      // Erfolgreiche Antwort auf 'true' mappen
      map(() => true),
    );
  }
}
